import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Crown, TrendingUp, Eye, Bell, Briefcase, 
  Star, Zap, Shield, ChevronRight, Sparkles,
  BarChart3, Target, Clock, Award
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PremiumDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [premiumJobs, setPremiumJobs] = useState([]);
  const [visibility, setVisibility] = useState('standard');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPremiumStats();
      fetchPremiumJobs();
    }
  }, [user]);

  const fetchPremiumStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/premium-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      setVisibility(response.data.profile_visibility || 'standard');
    } catch (error) {
      console.error('Erreur stats premium:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPremiumJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/jobs/premium`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPremiumJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Erreur jobs premium:', error);
    }
  };

  const updateVisibility = async (newVisibility) => {
    if (!user?.is_premium) {
      toast.error('Fonctionnalité réservée aux utilisateurs Premium');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/premium-visibility?visibility=${newVisibility}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVisibility(newVisibility);
      toast.success('Visibilité mise à jour!');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch {
      return 'Récemment';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  const isPremium = user?.is_premium;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Premium Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 
                  className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  {isPremium ? 'Dashboard Premium' : 'Découvrez Premium'}
                </h1>
                {isPremium && (
                  <Badge className="bg-gradient-to-r from-yellow-400 to-amber-500 text-slate-900 font-bold px-3 py-1">
                    <Crown className="h-4 w-4 mr-1" />
                    PREMIUM
                  </Badge>
                )}
              </div>
              <p className="text-slate-600">
                {isPremium 
                  ? 'Profitez de vos avantages exclusifs' 
                  : 'Boostez votre recherche d\'emploi avec Premium'
                }
              </p>
            </div>
            
            {!isPremium && (
              <Button
                onClick={() => navigate('/checkout', { state: { amount: 1.0, paymentType: 'subscription' }})}
                className="bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-slate-900 font-bold shadow-lg"
              >
                <Crown className="h-5 w-5 mr-2" />
                Passer à Premium - 1 USD/mois
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className={`p-6 ${isPremium ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Vues du profil</p>
                <p className="text-3xl font-bold text-slate-900">{stats?.profile_views || 0}</p>
                {isPremium && (
                  <p className="text-xs text-green-600 mt-1">+12% ce mois</p>
                )}
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isPremium ? 'bg-yellow-100' : 'bg-slate-100'}`}>
                <Eye className={`h-6 w-6 ${isPremium ? 'text-yellow-600' : 'text-slate-400'}`} />
              </div>
            </div>
          </Card>

          <Card className={`p-6 ${isPremium ? 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Classement</p>
                <p className="text-3xl font-bold text-slate-900">Top {stats?.ranking_percentile || 50}%</p>
                {isPremium && (
                  <p className="text-xs text-purple-600 mt-1">Parmi les candidats</p>
                )}
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isPremium ? 'bg-purple-100' : 'bg-slate-100'}`}>
                <Award className={`h-6 w-6 ${isPremium ? 'text-purple-600' : 'text-slate-400'}`} />
              </div>
            </div>
          </Card>

          <Card className={`p-6 ${isPremium ? 'bg-gradient-to-br from-sky-50 to-blue-50 border-sky-200' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Emplois correspondants</p>
                <p className="text-3xl font-bold text-slate-900">{stats?.matching_jobs_count || 0}</p>
                {isPremium && (
                  <p className="text-xs text-sky-600 mt-1">Selon vos préférences</p>
                )}
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isPremium ? 'bg-sky-100' : 'bg-slate-100'}`}>
                <Target className={`h-6 w-6 ${isPremium ? 'text-sky-600' : 'text-slate-400'}`} />
              </div>
            </div>
          </Card>

          <Card className={`p-6 ${isPremium ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Notifications</p>
                <p className="text-3xl font-bold text-slate-900">{stats?.notifications_this_month || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Ce mois</p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isPremium ? 'bg-green-100' : 'bg-slate-100'}`}>
                <Bell className={`h-6 w-6 ${isPremium ? 'text-green-600' : 'text-slate-400'}`} />
              </div>
            </div>
          </Card>
        </div>

        {/* Premium Benefits */}
        {isPremium ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Visibility Control */}
            <Card className="p-6 col-span-1">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-yellow-500" />
                Visibilité du profil
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Augmentez votre visibilité auprès des recruteurs
              </p>
              <Select value={visibility} onValueChange={updateVisibility}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">
                    <div className="flex items-center gap-2">
                      <span>Standard</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="enhanced">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-yellow-500" />
                      <span>Amélioré (+50% visibilité)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="featured">
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span>Mis en avant (+100% visibilité)</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Conseil:</strong> Un profil "Mis en avant" apparaît en priorité aux recruteurs.
                </p>
              </div>
            </Card>

            {/* Premium Features Status */}
            <Card className="p-6 col-span-1 lg:col-span-2">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-500" />
                Vos avantages Premium actifs
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Bell className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Notifications prioritaires</p>
                    <p className="text-xs text-slate-600">Recevez les offres en premier</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Eye className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Visibilité accrue</p>
                    <p className="text-xs text-slate-600">Profil mis en avant</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <BarChart3 className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Statistiques avancées</p>
                    <p className="text-xs text-slate-600">Analysez votre performance</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Shield className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Support prioritaire</p>
                    <p className="text-xs text-slate-600">Assistance dédiée</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        ) : (
          /* Non-Premium: Show benefits to encourage upgrade */
          <Card className="p-8 mb-8 bg-gradient-to-r from-yellow-50 via-amber-50 to-orange-50 border-yellow-200">
            <div className="text-center mb-8">
              <Crown className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Passez à Mosala Premium
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Démarquez-vous des autres candidats et accédez à des fonctionnalités exclusives pour booster votre recherche d'emploi.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Bell className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Notifications prioritaires</h3>
                <p className="text-sm text-slate-600">Recevez les nouvelles offres avant tout le monde</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Eye className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Profil mis en avant</h3>
                <p className="text-sm text-slate-600">Apparaissez en premier auprès des recruteurs</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <BarChart3 className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Statistiques détaillées</h3>
                <p className="text-sm text-slate-600">Suivez qui consulte votre profil</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Zap className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Accès anticipé</h3>
                <p className="text-sm text-slate-600">Nouvelles fonctionnalités en avant-première</p>
              </div>
            </div>
            
            <div className="text-center">
              <Button
                onClick={() => navigate('/checkout', { state: { amount: 1.0, paymentType: 'subscription' }})}
                className="bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-slate-900 font-bold px-8 py-6 text-lg shadow-xl"
              >
                <Crown className="h-5 w-5 mr-2" />
                Devenir Premium - Seulement 1 USD/mois
              </Button>
              <p className="text-sm text-slate-500 mt-3">Annulez à tout moment. Satisfait ou remboursé.</p>
            </div>
          </Card>
        )}

        {/* Recommended Jobs for Premium */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              {isPremium ? (
                <>
                  <Star className="h-5 w-5 text-yellow-500" />
                  Emplois recommandés pour vous
                </>
              ) : (
                <>
                  <Briefcase className="h-5 w-5 text-slate-400" />
                  Emplois récents
                </>
              )}
            </h3>
            <Button 
              variant="ghost" 
              className="text-sky-600"
              onClick={() => navigate('/jobs')}
            >
              Voir tout <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          {premiumJobs.length === 0 ? (
            <div className="text-center py-8">
              <Target className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Configurez vos préférences pour voir des emplois personnalisés</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate('/dashboard')}
              >
                Configurer mes préférences
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {premiumJobs.slice(0, 5).map((job) => (
                <div 
                  key={job.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                    job.featured ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-slate-200'
                  }`}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-900">{job.titre}</h4>
                        {job.featured && (
                          <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                            <Star className="h-3 w-3 mr-1" />
                            Featured
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{job.entreprise} • {job.localisation}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-3 w-3" />
                          {job.type_contrat}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {getTimeAgo(job.date_publication)}
                        </span>
                        {job.views > 0 && (
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {job.views} vues
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge className="bg-sky-100 text-sky-700">{job.domaine}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PremiumDashboard;
